import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ProductGrid } from '../components/products/ProductGrid';
import { Button } from '../components/ui/button';
import { ArrowLeft, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookSubcategory = () => {
  const { subcategoryName } = useParams();
  const [products, setProducts] = useState([]);
  const [subcategory, setSubcategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch subcategories to get the proper name
        const subcatRes = await axios.get(`${API}/book-subcategories`);
        const subcats = subcatRes.data;
        
        // Find matching subcategory
        const matchedSubcat = subcats.find(s => 
          s.name.toLowerCase().replace(/\s+/g, '-') === subcategoryName?.toLowerCase()
        );
        
        if (matchedSubcat) {
          setSubcategory(matchedSubcat);
          // Fetch products with this subcategory
          const productsRes = await axios.get(`${API}/products?subcategory_name=${subcategoryName}`);
          setProducts(productsRes.data);
        } else {
          // No matching subcategory, show all books
          const booksCategory = await axios.get(`${API}/categories`);
          const booksCat = booksCategory.data.find(c => c.name.toLowerCase() === 'books');
          if (booksCat) {
            const productsRes = await axios.get(`${API}/products?category_id=${booksCat.id}`);
            setProducts(productsRes.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [subcategoryName]);

  // Format display name
  const displayName = subcategory?.name || 
    (subcategoryName ? subcategoryName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Books');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="subcategory-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-primary">Home</Link>
        <span>/</span>
        <Link to="/category/books" className="hover:text-primary">Books</Link>
        <span>/</span>
        <span className="text-foreground">{displayName}</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="h-8 w-8 text-primary" />
          <h1 className="font-serif text-3xl font-bold text-foreground">{displayName}</h1>
        </div>
        <p className="text-muted-foreground">
          {products.length} {products.length === 1 ? 'book' : 'books'} found
        </p>
      </div>

      {/* Products Grid */}
      <ProductGrid products={products} loading={loading} />

      {/* Empty state */}
      {!loading && products.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-serif text-xl font-bold mb-2">No books in this category yet</h2>
          <p className="text-muted-foreground mb-4">Check back soon or browse our other collections</p>
          <Link to="/products">
            <Button className="btn-primary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Browse All Products
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default BookSubcategory;
